import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  HeartPulse, 
  LogOut, 
  Calendar,
  Clock,
  User,
  Stethoscope,
  CheckCircle2,
  AlertCircle,
  Baby,
  Activity,
  Thermometer,
  Wind,
  Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalAssistantDashboard = () => {
  const navigate = useNavigate();
  
  // Helper to get today's date in YYYY-MM-DD format (local timezone)
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [user, setUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Initialize with local timezone date
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const currentDate = `${year}-${month}-${day}`;
    console.log(`🕐 Medical Assistant Dashboard initialized with date: ${currentDate} (${now.toLocaleString()})`);
    return currentDate;
  });
  const [doctors, setDoctors] = useState([]); // Store doctor details for name lookup
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const refreshIntervalRef = useRef(null);
  
  // Vitals Dialog State
  const [showVitalsDialog, setShowVitalsDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [vitalsForm, setVitalsForm] = useState({
    temperature: '',
    pulse: '',
    respiratory_rate: '',
    spo2: '',
    blood_pressure: '',
    height: '',
    weight: ''
  });
  const [savingVitals, setSavingVitals] = useState(false);
  const [existingVitals, setExistingVitals] = useState(null);

  // Load user data on mount
  useEffect(() => {
    const userData = localStorage.getItem('healthcareUser');
    const hospitalData = localStorage.getItem('currentHospital');
    
    if (!userData || !hospitalData) {
      toast.error('Please login first');
      navigate('/medical-assistant-login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'medical_assistant') {
      toast.error('Unauthorized access');
      navigate('/');
      return;
    }
    
    setUser(parsedUser);
    setHospital(JSON.parse(hospitalData));
    
    // Fetch doctors to show names instead of IDs
    const fetchDoctors = async () => {
      try {
        const response = await axios.get(`${API}/doctors`);
        setDoctors(response.data || []);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      }
    };
    fetchDoctors();
  }, [navigate]);

  // Fetch appointments for assigned doctors
  const fetchAppointments = useCallback(async (silent = false) => {
    if (!user) return;
    
    if (!silent) setLoading(true);
    try {
      // Fetch all appointments for assigned doctors (no date filter on server)
      // This matches how FrontDesk and Doctor Dashboard fetch appointments
      const response = await axios.get(`${API}/medical-assistant/appointments/${user.id}`);
      setAppointments(response.data || []);
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error fetching appointments:', error);
      if (!silent) toast.error('Failed to load appointments');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user]);

  // Helper function to normalize date for comparison (handles both YYYY-MM-DD and DD/MM/YYYY)
  const normalizeDateForComparison = (dateStr) => {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format
    if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return dateStr;
    }
    // If in DD/MM/YYYY format, convert to YYYY-MM-DD
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    return dateStr;
  };

  // Filter appointments by selected date (client-side filtering like FrontDesk)
  const filteredAppointments = appointments.filter(apt => 
    normalizeDateForComparison(apt.appointment_date) === selectedDate
  );

  // Auto-refresh appointments every 30 seconds
  useEffect(() => {
    if (user) {
      fetchAppointments();
      
      // Set up auto-refresh interval
      refreshIntervalRef.current = setInterval(() => {
        fetchAppointments(true); // Silent refresh
      }, 30000); // 30 seconds
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [user, fetchAppointments]);

  // Handle logout
  const handleLogout = async () => {
    // Log logout event
    if (user?.loginId) {
      try {
        await axios.put(`${API}/security/logout/${user.loginId}`);
      } catch (error) {
        console.error('Failed to log logout:', error);
      }
    }
    
    localStorage.removeItem('healthcareUser');
    localStorage.removeItem('currentHospital');
    toast.success('Logged out successfully');
    navigate('/medical-assistant-login');
  };

  // Blood Pressure input handler - auto-insert "/" after 3 digits
  const handleBPInput = (e) => {
    let value = e.target.value.replace(/[^\d/]/g, ''); // Remove non-digits and non-slash
    
    // Auto-insert "/" after 3 digits (systolic)
    if (value.length === 3 && !value.includes('/')) {
      value = value + '/';
    }
    
    // Limit to format: XXX/XXX (max 7 characters)
    if (value.length > 7) {
      value = value.substring(0, 7);
    }
    
    setVitalsForm(prev => ({ ...prev, blood_pressure: value }));
  };

  // Blood Pressure keydown handler for smooth backspace on "/"
  const handleBPKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const value = vitalsForm.blood_pressure;
      const cursorPosition = e.target.selectionStart;
      
      // If cursor is right after "/" (e.g., "120/|"), delete the "/" and the digit before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        // Remove the "/" and the digit before it
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setVitalsForm(prev => ({ ...prev, blood_pressure: newValue }));
      }
    }
  };

  // Open vitals dialog for an appointment
  const openVitalsDialog = async (appointment) => {
    setSelectedAppointment(appointment);
    
    // Reset form
    setVitalsForm({
      temperature: '',
      pulse: '',
      respiratory_rate: '',
      spo2: '',
      blood_pressure: '',
      height: '',
      weight: ''
    });
    setExistingVitals(null);
    
    // Load existing vitals if available
    if (appointment.vitals) {
      setExistingVitals(appointment.vitals);
      setVitalsForm({
        temperature: appointment.vitals.temperature || '',
        pulse: appointment.vitals.pulse || '',
        respiratory_rate: appointment.vitals.respiratory_rate || '',
        spo2: appointment.vitals.spo2 || '',
        blood_pressure: appointment.vitals.blood_pressure || '',
        height: appointment.vitals.height || '',
        weight: appointment.vitals.weight || ''
      });
    }
    
    setShowVitalsDialog(true);
  };

  // Save vitals
  const saveVitals = async () => {
    if (!selectedAppointment) return;
    
    // Validate at least one vital is entered
    const hasValue = Object.values(vitalsForm).some(v => v !== '');
    if (!hasValue) {
      toast.error('Please enter at least one vital sign');
      return;
    }
    
    setSavingVitals(true);
    try {
      const vitalsData = {
        appointment_id: selectedAppointment.id,
        baby_id: selectedAppointment.baby_id,
        temperature: vitalsForm.temperature ? parseFloat(vitalsForm.temperature) : null,
        pulse: vitalsForm.pulse ? parseInt(vitalsForm.pulse) : null,
        respiratory_rate: vitalsForm.respiratory_rate ? parseInt(vitalsForm.respiratory_rate) : null,
        spo2: vitalsForm.spo2 ? parseInt(vitalsForm.spo2) : null,
        blood_pressure: vitalsForm.blood_pressure || null,
        height: vitalsForm.height ? parseFloat(vitalsForm.height) : null,
        weight: vitalsForm.weight ? parseFloat(vitalsForm.weight) : null,
        entered_by_id: user.id,
        entered_by_name: user.name
      };
      
      if (existingVitals) {
        // Update existing vitals
        await axios.put(`${API}/vitals-records/${existingVitals.id}?entered_by_id=${user.id}`, {
          temperature: vitalsData.temperature,
          pulse: vitalsData.pulse,
          respiratory_rate: vitalsData.respiratory_rate,
          spo2: vitalsData.spo2,
          blood_pressure: vitalsData.blood_pressure,
          height: vitalsData.height,
          weight: vitalsData.weight
        });
        toast.success('Vitals updated successfully');
      } else {
        // Create new vitals record
        await axios.post(`${API}/vitals-records`, vitalsData);
        toast.success('Vitals recorded successfully');
      }
      
      setShowVitalsDialog(false);
      fetchAppointments(); // Refresh list
    } catch (error) {
      console.error('Error saving vitals:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to save vitals');
      }
    } finally {
      setSavingVitals(false);
    }
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      // If already has AM/PM, just return it cleaned up
      if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
        return timeStr.toUpperCase();
      }
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    
    // Parse date - handle both ISO and DD/MM/YYYY formats
    let birthDate;
    if (dob.includes('/')) {
      const parts = dob.split('/');
      if (parts.length === 3) {
        // DD/MM/YYYY format
        birthDate = new Date(parts[2], parts[1] - 1, parts[0]);
      }
    } else {
      birthDate = new Date(dob);
    }
    
    if (isNaN(birthDate.getTime())) return 'N/A';
    
    const today = new Date();
    const diffTime = today - birthDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'N/A';
    
    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      return months > 0 ? `${years}y ${months}m` : `${years} year${years > 1 ? 's' : ''}`;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // Get doctor name with specialization
  const getDoctorDisplay = (docId) => {
    const doc = doctors.find(d => d.id === docId);
    if (doc) {
      return `Dr. ${doc.name}${doc.specialization ? ` (${doc.specialization})` : ''}`;
    }
    return docId;
  };

  // Format date for display with ordinal (e.g., "Saturday, 10th January 2026")
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    // Get ordinal suffix
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    return `${weekday}, ${getOrdinal(day)} ${month} ${year}`;
  };

  if (!user || !hospital) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50" data-testid="medical-assistant-dashboard">
      {/* Header */}
      {/* Header - Matching FrontDesk Layout */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-6 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                Medical Assistant Dashboard
              </h1>
              <div className="flex items-start space-x-3 md:space-x-4 mt-1">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-pink-100 rounded-xl flex items-center justify-center mt-1">
                  <HeartPulse className="w-5 h-5 md:w-6 md:h-6 text-pink-600" />
                </div>
                <div>
                  {hospital && (
                    <p className="text-gray-700 text-sm md:text-lg font-bold">{hospital.name}</p>
                  )}
                  <p className="text-gray-500 text-xs md:text-base font-medium">{user.name}</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleLogout}
              variant="outline"
              className="px-2 md:px-4"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Date Selector Bar - Matching FrontDesk Tab Bar Style */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 sm:py-4">
            <div className="flex items-center gap-3">
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto h-9"
                data-testid="date-selector"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(getTodayDate())}
              >
                Today
              </Button>
            </div>
            
            <div className="flex flex-col items-end gap-1">
              {lastSyncTime && (
                <span className="text-gray-400 text-xs">
                  Last synced: {lastSyncTime.toLocaleTimeString()} • Auto-refresh every 30s
                </span>
              )}
              <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-600 font-medium">
                <span className="text-green-600">✓ {filteredAppointments.filter(a => a.has_vitals).length} Recorded</span>
                <span className="text-yellow-600">○ {filteredAppointments.filter(a => !a.has_vitals && a.status !== 'cancelled').length} Pending</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 py-6">

        {/* Appointments List */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-purple-600" />
              Appointments for {formatDateDisplay(selectedDate)}
            </h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading appointments...</p>
            </div>
          ) : filteredAppointments.length === 0 ? (
            <div className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No appointments found for this date</p>
              <p className="text-sm text-gray-400 mt-1">
                {user.doctor_ids?.length === 0 
                  ? 'You are not assigned to any doctors yet' 
                  : 'Try selecting a different date'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => openVitalsDialog(appointment)}
                  data-testid={`appointment-card-${appointment.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2">
                        <h3 className="font-medium text-gray-900 flex items-center flex-wrap gap-2">
                          <span>{appointment.baby?.name || 'Unknown Patient'}</span>
                          {(appointment.baby?.short_id || appointment.baby?.id) && (
                            <span className="text-gray-500 text-sm font-normal">
                              ({appointment.baby.short_id || appointment.baby.id.substring(0, 8).toUpperCase()})
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                            {appointment.status || 'Scheduled'}
                          </span>
                        </h3>
                        <p className="text-sm text-gray-500">
                          {appointment.baby?.gender || 'N/A'} • {calculateAge(appointment.baby?.date_of_birth)}
                        </p>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatTime(appointment.appointment_time)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>Dr. {appointment.doctor?.name || 'Unknown'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {appointment.has_vitals ? (
                        <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-sm font-medium">Vitals Recorded</span>
                        </div>
                      ) : appointment.status !== 'cancelled' ? (
                        <Button
                          size="sm"
                          className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            openVitalsDialog(appointment);
                          }}
                          data-testid={`record-vitals-btn-${appointment.id}`}
                        >
                          <Activity className="w-4 h-4 mr-1" />
                          Record Vitals
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-400">Cancelled</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Vitals Dialog */}
      <Dialog open={showVitalsDialog} onOpenChange={setShowVitalsDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="vitals-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-pink-600" />
              {existingVitals ? 'Edit Vitals' : 'Record Vitals'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4">
              {/* Patient Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div>
                  <p className="font-medium text-gray-900">
                    {selectedAppointment.baby?.name}
                    {(selectedAppointment.baby?.short_id || selectedAppointment.baby?.id) && (
                      <span className="text-gray-500 text-sm font-normal ml-2">
                        ({selectedAppointment.baby.short_id || selectedAppointment.baby.id.substring(0, 8).toUpperCase()})
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    {selectedAppointment.baby?.gender} • {calculateAge(selectedAppointment.baby?.date_of_birth)} • 
                    Dr. {selectedAppointment.doctor?.name}
                  </p>
                </div>
              </div>
              
              {/* Vitals cannot be edited warning */}
              {existingVitals && !existingVitals.is_editable && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Doctor has started the session. Vitals can no longer be edited.
                </div>
              )}
              
              {/* Vitals Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Thermometer className="w-4 h-4 text-orange-500" />
                    Temperature (°F)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder={existingVitals && !existingVitals.is_editable ? "" : "98.6"}
                    value={vitalsForm.temperature}
                    onChange={(e) => setVitalsForm(prev => ({ ...prev, temperature: e.target.value }))}
                    disabled={existingVitals && !existingVitals.is_editable}
                    data-testid="input-temperature"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Heart className="w-4 h-4 text-red-500" />
                    Pulse (bpm)
                  </Label>
                  <Input
                    type="number"
                    placeholder={existingVitals && !existingVitals.is_editable ? "" : "80"}
                    value={vitalsForm.pulse}
                    onChange={(e) => setVitalsForm(prev => ({ ...prev, pulse: e.target.value }))}
                    disabled={existingVitals && !existingVitals.is_editable}
                    data-testid="input-pulse"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Wind className="w-4 h-4 text-blue-500" />
                    Respiratory Rate
                  </Label>
                  <Input
                    type="number"
                    placeholder={existingVitals && !existingVitals.is_editable ? "" : "16"}
                    value={vitalsForm.respiratory_rate}
                    onChange={(e) => setVitalsForm(prev => ({ ...prev, respiratory_rate: e.target.value }))}
                    disabled={existingVitals && !existingVitals.is_editable}
                    data-testid="input-respiratory-rate"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Activity className="w-4 h-4 text-green-500" />
                    SpO2 (%)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder={existingVitals && !existingVitals.is_editable ? "" : "98"}
                    value={vitalsForm.spo2}
                    onChange={(e) => setVitalsForm(prev => ({ ...prev, spo2: e.target.value }))}
                    disabled={existingVitals && !existingVitals.is_editable}
                    data-testid="input-spo2"
                  />
                </div>
                
                <div className="space-y-2 col-span-2">
                  <Label className="text-sm">Blood Pressure</Label>
                  <Input
                    type="text"
                    placeholder={existingVitals && !existingVitals.is_editable ? "" : "120/80"}
                    value={vitalsForm.blood_pressure}
                    onChange={handleBPInput}
                    onKeyDown={handleBPKeyDown}
                    disabled={existingVitals && !existingVitals.is_editable}
                    data-testid="input-blood-pressure"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Height (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder={existingVitals && !existingVitals.is_editable ? "" : "120"}
                    value={vitalsForm.height}
                    onChange={(e) => setVitalsForm(prev => ({ ...prev, height: e.target.value }))}
                    disabled={existingVitals && !existingVitals.is_editable}
                    data-testid="input-height"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder={existingVitals && !existingVitals.is_editable ? "" : "25"}
                    value={vitalsForm.weight}
                    onChange={(e) => setVitalsForm(prev => ({ ...prev, weight: e.target.value }))}
                    disabled={existingVitals && !existingVitals.is_editable}
                    data-testid="input-weight"
                  />
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowVitalsDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveVitals}
                  disabled={savingVitals || (existingVitals && !existingVitals.is_editable)}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                  data-testid="save-vitals-button"
                >
                  {savingVitals ? 'Saving...' : existingVitals ? 'Update Vitals' : 'Save Vitals'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicalAssistantDashboard;
